<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ComposeTeamAPI from 'dashboard/api/composeTeam';

const props = defineProps({
  selectedTeamId: {
    type: [Number, String],
    default: '',
  },
  showDropdown: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['updateTeam', 'toggleDropdown']);

const { t } = useI18n();
const teams = ref([]);
const isFetching = ref(false);

onMounted(async () => {
  isFetching.value = true;
  try {
    teams.value = await ComposeTeamAPI.getTeams();
  } finally {
    isFetching.value = false;
  }
});

const emptyValue = '';

const selectedTeam = computed(() =>
  teams.value.find(team => String(team.id) === String(props.selectedTeamId))
);

const selectedLabel = computed(() =>
  selectedTeam.value ? selectedTeam.value.name : t('COMPOSE_TEAM.EMPTY')
);

const menuItems = computed(() => {
  const empty = {
    label: t('COMPOSE_TEAM.EMPTY'),
    value: emptyValue,
    action: 'select',
  };
  const items = teams.value.map(team => ({
    label: team.name,
    value: team.id,
    action: 'select',
  }));
  return [empty, ...items];
});

const handleAction = item => {
  const value = item?.value === emptyValue || item?.value == null ? '' : item.value;
  emit('updateTeam', value);
  emit('toggleDropdown', false);
};
</script>

<template>
  <div
    class="flex items-center flex-1 w-full gap-3 px-4 py-3 overflow-y-visible"
  >
    <label class="mb-0.5 text-sm font-medium text-n-slate-11 whitespace-nowrap">
      {{ t('COMPOSE_TEAM.LABEL') }}
    </label>
    <div
      v-if="selectedTeam"
      class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 truncate ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1 h-7 min-w-0"
    >
      <span class="text-sm truncate text-n-slate-12">
        {{ selectedLabel }}
      </span>
      <Button
        variant="ghost"
        icon="i-lucide-x"
        color="slate"
        size="xs"
        class="flex-shrink-0"
        @click="emit('updateTeam', '')"
      />
    </div>
    <div
      v-else
      v-on-click-outside="() => emit('toggleDropdown', false)"
      class="relative flex items-center h-7"
    >
      <Spinner v-if="isFetching" :size="16" />
      <Button
        v-else
        :label="t('COMPOSE_TEAM.EMPTY')"
        variant="link"
        size="sm"
        color="slate"
        class="hover:!no-underline"
        @click="emit('toggleDropdown', !showDropdown)"
      />
      <DropdownMenu
        v-if="showDropdown"
        :menu-items="menuItems"
        class="ltr:left-0 rtl:right-0 z-[100] top-8 max-h-56 w-fit max-w-sm dark:!outline-n-slate-5"
        @action="handleAction"
      />
    </div>
  </div>
</template>
